import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Layout from "../components/Layout";
import userIcon from "../assets/userIcon.svg";
import driverImg from "../assets/driverImgFull.png";
import api from "../api";
import { GoogleMap, DirectionsRenderer } from "@react-google-maps/api";
import GooglePlacesInput from "../components/GooglePlacesInput";
import CustomAutoComplete from "../components/CustomAutoComplete";
import DriverModal from "../components/dashboard/DriverModal";
import CarModal from "../components/dashboard/CarModal";
import CustomTextInput from "../components/CustomTextInput";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Spinner from "../components/Spinner";
import CustomSelect from "../components/CustomSelect";
import CurrencyInput from "../components/CurrencyInput";
import CustomPhoneInput from "../components/CustomPhoneInput";

export default function RideDetailsPage() {
  const { id } = useParams();
  const [ride, setRide] = useState(null);
  const [history, setHistory] = useState(null);
  const [clients, setClients] = useState([]);
  const [client, setClient] = useState("");
  const [passenger, setPassenger] = useState("");
  const [passengerNumber, setPassengerNumber] = useState("");
  const [date, setDate] = useState("");
  const [hour, setHour] = useState("");
  const [status, setStatus] = useState("");
  const [pickup, setPickup] = useState("");
  const [destination, setDestination] = useState("");
  const [drivers, setDrivers] = useState([]);
  const [driver, setDriver] = useState("");
  const [cars, setCars] = useState([]);
  const [car, setCar] = useState("");
  const [voucher, setVoucher] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState(1);
  const [flightNumber, setFlightNumber] = useState("");

  const [obs, setObs] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  const [isAddNewModalOpen, setIsAddNewModalOpen] = useState(false);
  const [addNewType, setAddNewType] = useState("");
  const [newDriverAdded, setNewDriverAdded] = useState(false);
  const [newCarAdded, setNewCarAdded] = useState(false);

  const [fieldErrors, setFieldErrors] = useState({});
  const [update, setUpdate] = useState(false);

  const [directionsResponse, setDirectionsResponse] = useState(null);

  const statusOptions = [
    { value: 1, label: "Agendado" },
    { value: 2, label: "Pendiente" },
    { value: 3, label: "En espera" },
    { value: 4, label: "Confirmado" },
    { value: 5, label: "Cerrado" },
    { value: 6, label: "Rechazado" },
    { value: 7, label: "Cancelado" },
  ];

  const estadoMapping = {
    1: "Agendado",
    2: "En espera",
    3: "Pendiente",
    4: "Confirmado",
    5: "Cerrado",
    6: "Rechazado",
    7: "Cancelado",
  };

  const mapContainerStyle = {
    width: "100%",
    height: "100%",
    borderRadius: 15,
  };

  useEffect(() => {
    async function fetchData() {
      try {
        // Realiza las llamadas API en paralelo donde sea posible
        const ridePromise = (async () => {
          const rideDetails = await api.rides.getOne(id);
          setRide(rideDetails);

          const rideExtraDetails = await api.rides.getDetails(id);
          setHistory(rideExtraDetails);
          setClient(rideDetails.client.id);
          setPassenger(rideDetails.passenger);
          setPassengerNumber(rideDetails.passengerContact);
          setDate(rideDetails.date);
          setHour(rideDetails.hour);
          setStatus(rideDetails.status);
          setPickup(rideDetails?.pickup);
          setDestination(rideDetails?.destination);
          setDriver(rideDetails.driver?.id);
          setCar(rideDetails.vehicle?.id);
          setVoucher(rideDetails.voucherNumber);
          setAmount(rideDetails.amount);
          setCurrency(rideDetails.currency !== null ? rideDetails.currency : 1);
          setObs(rideDetails.observations);
          setNotes(rideDetails.notes);
          setFlightNumber(rideDetails.flightNumber);
        })();

        const driversPromise = (async () => {
          const response = await api.drivers.getMulti();
          setDrivers(response);
        })();

        const carsPromise = (async () => {
          const response = await api.vehicles.getMulti();
          setCars(response);
        })();

        const clientsPromise = (async () => {
          const response = await api.clients.getMulti();
          setClients(response);
        })();

        // Espera a que todas las promesas se resuelvan
        await Promise.all([
          ridePromise,
          driversPromise,
          carsPromise,
          clientsPromise,
        ]);
        setError(""); // Limpia errores si todo va bien
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }

    fetchData();
  }, [update, newDriverAdded, newCarAdded]);

  const estadoColors = {
    1: "#A5D3FF",
    2: "#B0B0B0",
    3: "#FFA500",
    4: "#4AA785",
    5: "#D9D9D9",
    6: "#E66672",
  };

  const renderEstado = (estado) => (
    <div className="flex items-center space-x-2">
      <span style={{ color: estadoColors[estado] || "#D9D9D9" }}>
        {estadoMapping[estado] || "N/A"}
      </span>
    </div>
  );

  const calculateRoute = () => {
    if (!ride?.pickup || !ride?.destination) {
      console.error("Faltan datos para calcular la ruta.");
      return;
    }

    const directionsService = new window.google.maps.DirectionsService();
    directionsService.route(
      {
        origin: ride.pickup, // Dirección en texto
        destination: ride.destination, // Dirección en texto
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setDirectionsResponse(result);
        } else {
          console.error("Error al calcular la ruta:", status);
        }
      }
    );
  };

  const checkData = () => {
    if (!ride?.pickup || !ride?.destination || !ride.driver || !ride.vehicle) {
      toast.warning("Hay datos faltantes");
    }
  };

  useEffect(() => {
    if (ride) {
      calculateRoute();
      checkData();
    }
  }, [ride]);

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setFieldErrors({});

    const editFields = {
      client: client || ride.client.id,
      passenger: passenger || ride.passenger,
      passenger_contact: passengerNumber || ride.passengerContact,
      date: date || ride.date,
      hour: hour || ride.hour,
      status: status || ride.status,
      pickup: pickup || ride.pickup,
      destination: destination || ride.destination,
      driver: driver || ride.driver,
      vehicle: car || ride.vehicle,
      voucher_number: voucher || ride.voucherNumber,
      amount: +amount || +ride.amount,
      currency: +currency || +ride.currency,
      observations: obs || ride.observations,
      notes: notes || ride.notes,
      flight_number: flightNumber || ride.flightNumber,
    };

    try {
      await api.rides.update(id, editFields);
      setUpdate(true);
      toast.success("Datos guardados con éxito");
    } catch (error) {
      if (error.message) {
        // Ajuste para errores específicos de campo según la estructura proporcionada
        const fieldErrors = error.message;
        setFieldErrors(fieldErrors);
      } else {
        toast.error("Error al modeficar los campos. Intenta nuevamente.");
      }
    }
  };

  const handleAddNew = (type) => {
    setAddNewType(type); // Guardar el tipo de entidad
    setIsAddNewModalOpen(true); // Abrir el modal para agregar
  };

  const closeModal = () => {
    setAddNewType("");
    setIsAddNewModalOpen(false);
  };

  const handleDriverAdded = (isAdded) => {
    setNewDriverAdded(isAdded); // Actualiza el estado para indicar que se agregó un nuevo conductor
  };

  const handleCarAdded = (isAdded) => {
    setNewCarAdded(isAdded); // Actualiza el estado para indicar que se agregó un nuevo vehiculo
  };

  const [opacityLevel, setOpacityLevel] = useState(0);

  useEffect(() => {
    if (!ride) {
      let intervalId = setInterval(() => {
        setOpacityLevel((prev) => Math.min(prev + 0.1, 1)); // Incrementa la opacidad hasta 1
      }, 500); // Ajusta el intervalo para controlar la velocidad
      return () => clearInterval(intervalId); // Limpia el intervalo al desmontar
    } else {
      setOpacityLevel(1); // Asegúrate de que la opacidad esté al máximo al terminar de cargar
    }
  }, [ride]);

  if (!ride) {
    return <Spinner />;
  }

  return (
    <Layout>
      <div
        className="flex flex-col h-screen bg-[#F8F9FB] w-full md:p-8 transition-opacity ease-in-out"
        style={{
          opacity: opacityLevel,
          transition: "opacity 0.5s linear",
        }}
      >
        <ToastContainer />
        <h1 className="poppins-bold text-lg text-left text-[#1F384C] mb-6">
          Información del viaje
        </h1>
        {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
        <div className="flex w-full md:gap-20 bg-white p-4">
          {/* Información del Cliente */}
          <div className="mb-6 min-w-[158px]">
            <h3 className="inter-semibold text-sm text-[#A4A4A4] mb-2">
              Cliente ({ride.client.isCorporate ? "Corporativo" : "Particular"})
            </h3>
            <div className="flex gap-2 items-center">
              <div className="w-[42px] h-[42px]">
                <img
                  src={ride.client.logo}
                  alt="Cliente"
                  className="w-full h-full rounded-md object-cover"
                />
              </div>
              <div className="flex flex-col gap-1">
                <div>
                  <CustomAutoComplete
                    placeholder="Ingresa el nombre del cliente"
                    value={client}
                    onChange={(selectedId) => {
                      setClient(selectedId);
                      const selectedClient = clients.find(
                        (c) => c.id === selectedId
                      );
                      if (selectedClient?.phone) {
                        setPassengerNumber(selectedClient.phone);
                      }
                    }}
                    options={clients}
                    valueKey="id" // Clave para el valor
                    labelKey="name" // Clave para el texto visible
                    onAddNew={() => handleAddNew("client")}
                    isRequired
                  />
                  {fieldErrors.client && (
                    <p className="text-red-600 text-sm">
                      {fieldErrors.client[0]}
                    </p>
                  )}
                </div>
                <span className="inter-regular text-[10px] text-[#A4A4A4]">
                  {ride.client.ruc || "-"}
                </span>
              </div>
            </div>
          </div>

          {/* Información del Pasajero */}
          <div className="mb-6 min-w-[158px]">
            <h3 className="inter-semibold text-sm text-[#A4A4A4] mb-2">
              Pasajero
            </h3>
            <div className="flex gap-2 items-center">
              <div className="w-[42px] h-[42px]">
                <img
                  src={userIcon}
                  alt="Pasajero"
                  className="w-full h-full rounded-md object-cover"
                />
              </div>
              <div className="flex flex-col gap-1">
                <div>
                  <CustomTextInput
                    placeholder="Ingresa el nombre del pasajero"
                    type="text"
                    value={passenger}
                    onChange={(e) => setPassenger(e.target.value)}
                    isRequired
                  />
                  {fieldErrors.passenger && (
                    <p className="text-red-600 text-sm">
                      {fieldErrors.passenger[0]}
                    </p>
                  )}
                </div>

                {/*<span className="inter-regular text-[10px] text-[#A4A4A4]">
                  {ride.passenger.govId || "-"}
                </span>*/}
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <div>
              <CustomPhoneInput
                label="Teléfono del pasajero"
                value={passengerNumber}
                onChange={(phone) => setPassengerNumber(phone)}
              />
              {fieldErrors.passenger_contact && (
                <p className="text-red-600 text-sm">
                  {fieldErrors.passenger_contact[0]}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Nuevo contenedor */}
        <div className="flex flex-grow w-full mt-6 md:gap-14 wrap">
          {/* Contenedor izquierdo */}
          <div className="flex flex-col w-[60%] gap-4">
            <div className="flex flex-col gap-6 border border-[#C2C2C2] rounded-md p-4 bg-white">
              <p className="poppins-semibold text-xs text-[#ACACB5]">
                Información del viaje actual
              </p>

              <div className="grid grid-cols-2 gap-x-12 gap-y-3">
                <div>
                  <GooglePlacesInput
                    label="Lugar de recogida"
                    value={pickup}
                    onChange={setPickup}
                    placeholder="Ingresa el lugar de recogida"
                  />
                  {fieldErrors.pickup && (
                    <p className="text-red-600 text-sm">
                      {fieldErrors.pickup[0]}
                    </p>
                  )}
                </div>

                <div>
                  <GooglePlacesInput
                    label="Lugar de destino"
                    value={destination}
                    onChange={setDestination}
                    placeholder="Ingresa el lugar de destino"
                  />
                  {fieldErrors.destination && (
                    <p className="text-red-600 text-sm">
                      {fieldErrors.destination[0]}
                    </p>
                  )}
                </div>

                <div>
                  <CustomTextInput
                    label="Fecha"
                    placeholder="Ingresa la fecha"
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                  {fieldErrors.date && (
                    <p className="text-red-600 text-sm">
                      {fieldErrors.date[0]}
                    </p>
                  )}
                </div>

                <div>
                  <CustomTextInput
                    label="Hora"
                    placeholder="Ingresa la hora"
                    type="time"
                    value={hour}
                    onChange={(e) => setHour(e.target.value)}
                  />
                  {fieldErrors.hour && (
                    <p className="text-red-600 text-sm">
                      {fieldErrors.hour[0]}
                    </p>
                  )}
                </div>

                <div>
                  <CustomTextInput
                    label="Nº de voucher"
                    placeholder="Ingresa el nº de voucher"
                    type="text"
                    value={voucher}
                    onChange={(e) => setVoucher(e.target.value)}
                  />
                  {fieldErrors.voucherNumber && (
                    <p className="text-red-600 text-sm">
                      {fieldErrors.voucherNumber[0]}
                    </p>
                  )}
                </div>

                <div>
                  <CustomTextInput
                    label="Nº de vuelo"
                    placeholder="Ingresa el nº de vuelo"
                    type="text"
                    value={flightNumber}
                    onChange={(e) => setFlightNumber(e.target.value)}
                  />
                  {fieldErrors.flightNumber && (
                    <p className="text-red-600 text-sm">
                      {fieldErrors.flightNumber[0]}
                    </p>
                  )}
                </div>

                <div>
                  <CustomAutoComplete
                    label="Conductor asignado"
                    placeholder="Asigna al conductor"
                    value={driver}
                    onChange={setDriver}
                    options={drivers}
                    labelKey="name"
                    valueKey="id"
                    onAddNew={() => handleAddNew("driver")}
                  />
                  {fieldErrors.driver && (
                    <p className="text-red-600 text-sm">
                      {fieldErrors.driver[0]}
                    </p>
                  )}
                </div>

                <div>
                  <CustomAutoComplete
                    label="Vehículo asignado"
                    placeholder="Selecciona el vehículo"
                    type="select"
                    value={car}
                    onChange={setCar}
                    options={cars}
                    labelKey="name"
                    valueKey="id"
                    onAddNew={() => handleAddNew("car")}
                  />
                  {fieldErrors.vehicle && (
                    <p className="text-red-600 text-sm">
                      {fieldErrors.vehicle[0]}
                    </p>
                  )}
                </div>
                <div>
                  <CurrencyInput
                    label="Monto"
                    amount={amount}
                    setAmount={setAmount}
                    currency={currency}
                    setCurrency={setCurrency}
                  />
                  {fieldErrors.amount && (
                    <p className="text-red-600 text-sm">
                      {fieldErrors.amount[0]}
                    </p>
                  )}
                  {fieldErrors.currency && (
                    <p className="text-red-600 text-sm">
                      {fieldErrors.currency[0]}
                    </p>
                  )}
                </div>
                <div>
                  {/*<p className="inter-semibold text-[9px] text-[#848491]">
                      Estado
                    </p>
                    <p className="inter-regular text-[8px] text-[#797989]">
                      {renderEstado(ride.status)}
                    </p>*/}
                  <CustomTextInput
                    label="Estado"
                    placeholder="Selecciona el estado"
                    type="select"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    options={statusOptions}
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col flex-grow gap-6 border border-[#C2C2C2] rounded-md p-4">
              <p className="poppins-semibold text-xs text-[#ACACB5]">
                Historial de cambios
              </p>
              <div className="flex flex-col gap-6">
                {history?.changeHistory?.map((e) => (
                  <div className="flex justify-between" key={e.id}>
                    <div className="flex gap-2">
                      <div className="flex flex-col gap-1">
                        <p className="inter-semibold text-[9px] text-[#848491]">
                          Modificado por
                        </p>
                        <p className="inter-regular text-[8px] text-[#797989]">
                          {e.modifiedBy}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <div className="flex flex-col gap-1">
                        <p className="inter-semibold text-[9px] text-[#848491]">
                          Lugar de recogida
                        </p>
                        <div
                          className="relative group"
                          style={{
                            maxWidth: "150px", // Ajusta según sea necesario
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={e.pickup} // Tooltip nativo
                        >
                          <p className="inter-regular text-[8px] text-[#797989]">
                            {e.pickup && e.pickup.length > 20
                              ? `${e.pickup.slice(0, 20)}...`
                              : e.pickup}
                          </p>
                          {/* Tooltip personalizado */}
                          <div
                            className="absolute left-0 top-full mt-1 hidden group-hover:block bg-gray-800 text-white text-xs p-1 rounded shadow-lg"
                            style={{
                              whiteSpace: "normal",
                              maxWidth: "300px",
                              zIndex: 10,
                            }}
                          >
                            {e.pickup}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <div className="flex flex-col gap-1">
                        <p className="inter-semibold text-[9px] text-[#848491]">
                          Lugar de destino
                        </p>
                        <div
                          className="relative group"
                          style={{
                            maxWidth: "150px", // Ajusta según sea necesario
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={e.destination} // Tooltip nativo
                        >
                          <p className="inter-regular text-[8px] text-[#797989]">
                            {e.destination && e.destination.length > 20
                              ? `${e.destination.slice(0, 20)}...`
                              : e.destination}
                          </p>
                          {/* Tooltip personalizado */}
                          <div
                            className="absolute left-0 top-full mt-1 hidden group-hover:block bg-gray-800 text-white text-xs p-1 rounded shadow-lg"
                            style={{
                              whiteSpace: "normal",
                              maxWidth: "300px",
                              zIndex: 10,
                            }}
                          >
                            {e.destination}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <div className="flex flex-col gap-1">
                        <p className="inter-semibold text-[9px] text-[#848491]">
                          Estado
                        </p>
                        <p
                          className="inter-regular text-[8px] text-[#797989]"
                          style={{
                            color:
                              e.status === "Agendado"
                                ? "#A5D3FF"
                                : e.status === "En espera"
                                ? "#B0B0B0"
                                : e.status === "Pendiente"
                                ? "#FFA500"
                                : e.status === "Confirmado"
                                ? "#4AA785"
                                : e.status === "Rechazado"
                                ? "#E66672"
                                : e.status === "Cancelado"
                                ? "#BE202F"
                                : "#D9D9D9",
                          }}
                        >
                          {e.status}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <div className="flex flex-col gap-1">
                        <p className="inter-semibold text-[9px] text-[#848491]">
                          Fecha de modificación
                        </p>
                        <p className="inter-regular text-[8px] text-[#797989]">
                          {e.modifiedAt}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-3">
                {(() => {
                  const entries = [];

                  history?.changeHistory?.forEach((e, index, arr) => {
                    const prev = arr[index - 1];

                    const isVehicleChanged =
                      !prev || prev.vehicle !== e.vehicle;
                    const isDriverChanged = !prev || prev.driver !== e.driver;
                    const isStatusConfirmed =
                      (!prev || prev.status !== e.status) &&
                      e.status === "Confirmado";

                    if (isVehicleChanged) {
                      entries.push({
                        key: `vehicle-${index}`,
                        type: "vehicle",
                        modifiedAt: e.modifiedAt,
                        value: e.vehicle,
                      });
                    }

                    if (isDriverChanged) {
                      entries.push({
                        key: `driver-${index}`,
                        type: "driver",
                        modifiedAt: e.modifiedAt,
                        value: e.driver,
                      });
                    }

                    if (isStatusConfirmed) {
                      entries.push({
                        key: `status-${index}`,
                        type: "confirmed",
                        modifiedAt: e.modifiedAt,
                      });
                    }
                  });

                  // Ordenar por fecha ascendente
                  entries.sort(
                    (a, b) => new Date(a.modifiedAt) - new Date(b.modifiedAt)
                  );

                  return entries.map((e) => (
                    <div key={e.key} className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{
                            backgroundColor: "green",
                            display: "inline",
                          }}
                        ></span>

                        <p className="inter-medium text-[9px] text-[#848491]">
                          <span className="inter-bold">{e.modifiedAt}</span> :{" "}
                          {e.type === "vehicle" && (
                            <>
                              Se ha asignado el vehículo{" "}
                              <span className="inter-bold text-[#0080FF]">
                                {e.value}
                              </span>{" "}
                              como vehículo del viaje
                            </>
                          )}
                          {e.type === "driver" && (
                            <>
                              Se ha asignado a{" "}
                              <span className="inter-bold text-[#BE202F]">
                                {e.value}
                              </span>{" "}
                              como conductor del viaje
                            </>
                          )}
                          {e.type === "confirmed" && (
                            <>
                              El viaje ha sido{" "}
                              <span className="inter-bold text-green-600">
                                confirmado
                              </span>{" "}
                              por el conductor
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                  ));
                })()}
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex flex-col space-y-1">
                  <label
                    htmlFor="obs"
                    className="inter-medium text-[#949494] text-[9px] mb-1"
                  >
                    Observaciones
                  </label>
                  <textarea
                    placeholder="Deja las observaciones"
                    type="text"
                    id="obs"
                    value={obs}
                    onChange={(e) => setObs(e.target.value)}
                    className={`inter-regular w-[80%] py-4 px-4 border border-[#D3D3D3] rounded-[9px] bg-[#FFFFFF] text-[8px] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none`}
                  />
                </div>
                {/*<div className="flex flex-col space-y-1">
                  <label
                    htmlFor="notes"
                    className="inter-medium text-[#949494] text-[9px] mb-1"
                  >
                    Notas
                  </label>
                  <textarea
                    placeholder="Deja las notas"
                    type="text"
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className={`inter-regular w-[80%] py-4 px-4 border border-[#D3D3D3] rounded-[9px] bg-[#FFFFFF] text-[8px]  focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none`}
                  />
                </div>*/}
              </div>
              <button
                onClick={(e) => handleFormSubmit(e)}
                className="bg-[#BE202F] mt-auto text-white text-xs py-4 px-4 rounded-2xl tracking-wider"
              >
                Guardar cambios
              </button>
            </div>
          </div>

          {/* Contenedor derecho */}
          <div className="flex-grow rounded-md">
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              options={{
                fullscreenControl: true, // fullscreen button
                zoomControl: false, // zoom controls
                mapTypeControl: false, // map type control (Map/Satellite)
                streetViewControl: false, // Street View control
                scaleControl: false, // scale control
              }}
            >
              {directionsResponse && (
                <DirectionsRenderer directions={directionsResponse} />
              )}
            </GoogleMap>
          </div>
        </div>
        {/* Renderizado condicional del modal */}
        {isAddNewModalOpen && (
          <>
            {addNewType === "driver" && (
              <DriverModal
                isOpen={isAddNewModalOpen}
                onClose={closeModal}
                onDriverAdded={handleDriverAdded}
              />
            )}
            {addNewType === "car" && (
              <CarModal
                isOpen={isAddNewModalOpen}
                onClose={closeModal}
                onCarAdded={handleCarAdded}
              />
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
